"use client";

import { useMemo, useState } from "react";
import {
  ErrorNotice,
  Notice,
  SelectField,
} from "@/components/tool-kit/primitives";
import { AdjustedSummary } from "@/components/tools/stats-clinical/age-adjusted-results";
import { PasteBox } from "@/components/tools/stats-research/paste-box";
import { PreviewTable } from "@/components/tools/stats-research/preview-table";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  type AgeGroupInput,
  checkAgeGroups,
  directStandardization,
} from "@/lib/tool-kit/stats-clinical/age-adjusted";
import {
  AGE_GROUP_LABELS,
  STANDARD_POPULATIONS,
  type StandardId,
} from "@/lib/tool-kit/stats-clinical/standard-populations";
import {
  type HeaderMode,
  parseNumericTable,
} from "@/lib/tool-kit/stats-research/matrix";

/** English strings. A translated content file overrides these through `ui`. */
export const AGE_ADJUSTED_RATE_CALCULATOR_STRINGS = {
  dataLabel:
    "One row per age group, youngest first: cases (deaths), population or person-years — 18 rows from 0–4 to 85+",
  dataHint:
    "Paste from a spreadsheet, or separate values with tabs, commas or spaces. An age-group name in the first column is optional. Enter 0 cases for an age group with none. With your own standard, add a third column holding that standard's counts. The example uses made-up numbers.",
  dataPlaceholder: "0–4\t8\t5200\n5–9\t1\t5600\n10–14\t1\t5900",
  dataExample:
    "Age group\tDeaths\tPopulation\n0–4\t8\t5200\n5–9\t1\t5600\n10–14\t1\t5900\n15–19\t4\t6100\n20–24\t6\t6000\n25–29\t7\t6300\n30–34\t9\t6700\n35–39\t12\t6900\n40–44\t17\t6800\n45–49\t29\t6900\n50–54\t49\t7000\n55–59\t71\t6500\n60–64\t96\t5800\n65–69\t128\t4900\n70–74\t171\t3900\n75–79\t232\t3000\n80–84\t301\t2100\n85+\t373\t1500",
  loadExample: "Load example",
  clear: "Clear",
  headerLabel: "Header row",
  headerAuto: "Detect automatically",
  headerYes: "First row is a header",
  headerNo: "No header row",
  standardLabel: "Standard population",
  standardUs2000: "US 2000 standard population (NCHS, SEER)",
  standardWho: "WHO World Standard 2000–2025",
  standardEsp: "European Standard Population 2013 (Eurostat)",
  standardCustom: "Your own (third column)",
  nameUs2000: "US 2000",
  nameWho: "WHO 2000–2025",
  nameEsp: "ESP 2013",
  nameCustom: "your standard",
  rateBaseLabel: "Express rates per",
  rateBase1000: "1,000",
  rateBase10000: "10,000",
  rateBase100000: "100,000",
  levelLabel: "Confidence level",
  unreadable: "Line {line}, column {column}: “{text}” is not a number.",
  wrongLength:
    "The calculator needs 18 age groups (0–4, 5–9, … 80–84, 85+); this data has {count}. Enter 0 cases for any group with none, and combine finer groups into these.",
  badRow:
    "Line {line}: cases must be a whole number of zero or more, and the population must be above zero.",
  needColumns:
    "Each row needs cases and a population; with your own standard, a third number too.",
  badStandard:
    "Your standard population counts must be zero or more, and not all zero.",
  enterPrompt:
    "Paste cases and populations by age group to get the age-adjusted rate with a confidence interval.",
  previewCaption: "The standard population's weights",
  previewHint: "The age-group weights the standard applies to your rates.",
  results: "Results",
  cardAdjusted: "Age-adjusted rate per {base}",
  cardInterval: "{level}% CI (Fay–Feuer)",
  cardCrude: "Crude rate",
  reading:
    "Weighting your age-specific rates to the {standard} age structure gives an age-adjusted rate of {adjusted} per {base}, against a crude rate of {crude} ({cases} cases in all). Adjusted rates are for comparison between populations that use the same standard; they are not a count of real events.",
  columnAge: "Age group",
  columnCases: "Cases",
  columnPopulation: "Population",
  columnRate: "Rate per {base}",
  columnWeight: "Weight",
  columnContribution: "Weight × rate",
  total: "Total",
  alternativesHeading: "The same data under each standard",
  alternativesNote:
    "The adjusted rate depends on the standard, so compare only rates adjusted to the same one — and say which you used.",
  columnStandard: "Standard",
  columnAdjusted: "Adjusted rate per {base}",
  columnInterval: "{level}% CI",
  howCalculated: "How this was calculated",
  workingRate:
    "Adjusted rate = Σ wᵢ × (casesᵢ / populationᵢ), where wᵢ is age group i's share of the standard population.",
  workingSe:
    "Standard error = √(Σ wᵢ² × casesᵢ / populationᵢ²) = {se} per {base}.",
  workingNote:
    "The confidence interval is the gamma method of Fay and Feuer (1997), as in NCI's SEER*Stat and R's epitools: it stays positive and has good coverage when counts are small. Counts are treated as Poisson.",
  report:
    "Age-adjusted rate ({standard} standard) {adjusted} per {base} (95% CI {lower} to {upper}); crude rate {crude}.",
  copyResult: "Copy result",
  copied: "Copied!",
} as const;

const BASES = [
  { value: "1000", key: "rateBase1000" },
  { value: "10000", key: "rateBase10000" },
  { value: "100000", key: "rateBase100000" },
] as const;

const LEVELS = ["0.9", "0.95", "0.99"] as const;
const BUILT_IN: readonly StandardId[] = ["us2000", "who", "esp2013"];

type Choice = StandardId | "custom";

const NAME_KEY = {
  us2000: "nameUs2000",
  who: "nameWho",
  esp2013: "nameEsp",
  custom: "nameCustom",
} as const;

interface Read {
  groups: AgeGroupInput[];
  custom: number[];
  problem: string | null;
}

function readTable(
  table: ReturnType<typeof parseNumericTable>,
  needCustom: boolean,
  strings: typeof AGE_ADJUSTED_RATE_CALCULATOR_STRINGS,
  fmt: (v: number) => string
): Read {
  const empty = { groups: [], custom: [] };
  const [issue] = table.unreadable;
  if (issue) {
    return {
      ...empty,
      problem: t(strings.unreadable, {
        line: fmt(issue.line),
        column: fmt(issue.column),
        text: issue.text,
      }),
    };
  }
  if (table.rows.length === 0) {
    return { ...empty, problem: null };
  }
  const groups: AgeGroupInput[] = [];
  const custom: number[] = [];
  for (const [index, row] of table.rows.entries()) {
    const [cases, population, standard] = row;
    if (
      cases == null ||
      population == null ||
      (needCustom && standard == null)
    ) {
      return { ...empty, problem: strings.needColumns };
    }
    if (!Number.isInteger(cases) || cases < 0 || !(population > 0)) {
      return {
        ...empty,
        problem: t(strings.badRow, { line: fmt(table.lines[index]) }),
      };
    }
    groups.push({
      label:
        table.rowLabels?.[index] ||
        AGE_GROUP_LABELS[index] ||
        String(index + 1),
      cases,
      population,
    });
    custom.push(standard ?? 0);
  }
  return { groups, custom, problem: null };
}

export default function AgeAdjustedRateCalculatorWidget() {
  const strings = useToolUi(AGE_ADJUSTED_RATE_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const [text, setText] = useState("");
  const [header, setHeader] = useState<HeaderMode>("auto");
  const [choice, setChoice] = useState<Choice>("us2000");
  const [base, setBase] = useState("100000");
  const [level, setLevel] = useState("0.95");

  const table = useMemo(() => parseNumericTable(text, header), [text, header]);
  const read = readTable(table, choice === "custom", strings, (v) => fmt(v));
  const standard =
    choice === "custom" ? read.custom : STANDARD_POPULATIONS[choice];
  let problem = read.problem;
  if (!problem && read.groups.length > 0) {
    const issue = checkAgeGroups(read.groups, standard);
    if (issue === "length") {
      problem = t(strings.wrongLength, { count: fmt(read.groups.length) });
    } else if (issue === "weights") {
      problem = strings.badStandard;
    }
  }
  const result =
    !problem && read.groups.length > 0
      ? directStandardization(
          read.groups,
          standard,
          Number(base),
          Number(level)
        )
      : null;
  const alternatives = result
    ? BUILT_IN.map((id) => ({
        name: strings[NAME_KEY[id]],
        result: directStandardization(
          read.groups,
          STANDARD_POPULATIONS[id],
          Number(base),
          Number(level)
        ),
      })).flatMap((a) => (a.result ? [{ name: a.name, result: a.result }] : []))
    : [];
  const entered = text.trim() !== "";
  const weights = choice === "custom" ? null : STANDARD_POPULATIONS[choice];
  const weightTotal = weights ? weights.reduce((s, v) => s + v, 0) : 1;

  return (
    <div className="space-y-6">
      <PasteBox
        actions={[
          {
            label: strings.loadExample,
            onClick: () => {
              setHeader("auto");
              setText(strings.dataExample);
              if (choice === "custom") {
                setChoice("us2000");
              }
            },
          },
          { label: strings.clear, onClick: () => setText("") },
        ]}
        header={{
          label: strings.headerLabel,
          value: header,
          onChange: setHeader,
          options: [
            { value: "auto", label: strings.headerAuto },
            { value: "yes", label: strings.headerYes },
            { value: "no", label: strings.headerNo },
          ],
        }}
        hint={strings.dataHint}
        id="aar-data"
        label={strings.dataLabel}
        onChange={setText}
        placeholder={strings.dataPlaceholder}
        rows={9}
        value={text}
      />
      <div className="grid gap-4 sm:grid-cols-3">
        <SelectField
          id="aar-standard"
          label={strings.standardLabel}
          onChange={setChoice}
          options={[
            { value: "us2000", label: strings.standardUs2000 },
            { value: "who", label: strings.standardWho },
            { value: "esp2013", label: strings.standardEsp },
            { value: "custom", label: strings.standardCustom },
          ]}
          value={choice}
        />
        <SelectField
          id="aar-base"
          label={strings.rateBaseLabel}
          onChange={setBase}
          options={BASES.map(({ value, key }) => ({
            value,
            label: strings[key],
          }))}
          value={base}
        />
        <SelectField
          id="aar-level"
          label={strings.levelLabel}
          onChange={setLevel}
          options={LEVELS.map((value) => ({
            value,
            label: `${fmt(Number(value) * 100)}%`,
          }))}
          value={level}
        />
      </div>
      {weights && !entered ? (
        <PreviewTable
          caption={`${strings.previewCaption}. ${strings.previewHint}`}
          head={[strings.columnWeight]}
          limit={20}
          rowHeads={[...AGE_GROUP_LABELS]}
          rows={weights.map((v) => [fixed4(fmt, v / weightTotal)])}
        />
      ) : null}
      {problem ? <ErrorNotice>{problem}</ErrorNotice> : null}
      {result ? (
        <AdjustedSummary
          alternatives={alternatives}
          baseText={fmt(Number(base))}
          fmt={fmt}
          result={result}
          standardName={strings[NAME_KEY[choice]]}
          strings={strings}
        />
      ) : null}
      {entered ? null : <Notice>{strings.enterPrompt}</Notice>}
    </div>
  );
}

function fixed4(
  fmt: (value: number, options?: Intl.NumberFormatOptions) => string,
  value: number
): string {
  return fmt(value, { minimumFractionDigits: 4, maximumFractionDigits: 4 });
}
